import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html, Line } from '@react-three/drei';

// helix points along Z-axis tube of radius R, from -len/2..len/2, pitch p (scene units)
function helixPoints(R, len, pitch, phase = 0) {
  const p = Math.max(0.5, pitch);
  const turns = Math.max(1, len / p);
  const segs = Math.max(60, Math.round(turns * 40));
  const pts = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const z = -len / 2 + t * len;
    const a = phase + (z / p) * Math.PI * 2;
    pts.push([Math.cos(a) * R, Math.sin(a) * R, z]);
  }
  return pts;
}

const tag = color => ({
  color, fontSize: 10.5, fontFamily: 'monospace', whiteSpace: 'nowrap',
  background: 'rgba(15,20,32,0.85)', padding: '1px 5px', borderRadius: 3, pointerEvents: 'none'
});

// Steel armour: metallic tube over the inner core, with helical tape-edge lines
// whose spacing = the (visual) lay/pitch, so you see the wrap change with pitch.
const Armour3D = ({ layer, pitchMm = 40, height = 420 }) => {
  if (!layer || layer.kind !== 'armour') {
    return <div style={{ height, background: '#0f1420', borderRadius: 6 }}
      className="d-flex align-items-center justify-content-center text-500">No armouring on this cable</div>;
  }
  const innerR = layer.innerD / 2;
  const outerR = layer.outerD / 2;
  // long enough that several wraps are always visible and the pitch visibly changes
  const p = pitchMm > 0 ? pitchMm : layer.wide * 2;
  const len = Math.max(p * 3, outerR * 22);
  const cam = Math.max(outerR * 7, len * 0.9);

  // two tape edges (each tape has a leading + trailing edge = a ribbon of width `wide`)
  const rSurf = outerR * 1.01;
  const edges = [
    { pts: helixPoints(rSurf, len, p, 0), c: '#ffcf5a' },
    { pts: helixPoints(rSurf, len, p, (layer.wide / p) * Math.PI * 2), c: '#e0a92e' },
    { pts: helixPoints(rSurf, len, p, Math.PI), c: '#ffcf5a' },
    { pts: helixPoints(rSurf, len, p, Math.PI + (layer.wide / p) * Math.PI * 2), c: '#e0a92e' }
  ];

  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [cam * 0.5, cam * 0.4, cam], fov: 40 }} dpr={[1, 2]}>
        <color attach="background" args={['#0f1420']} />
        <ambientLight intensity={0.8} />
        <directionalLight position={[outerR * 4, outerR * 6, outerR * 8]} intensity={1.15} />
        <directionalLight position={[-outerR * 4, 0, outerR * 4]} intensity={0.4} />
        <OrbitControls enablePan enableZoom />

        {/* inner core */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[innerR, innerR, len * 1.02, 48]} />
          <meshStandardMaterial color="#556" roughness={0.85} />
        </mesh>
        {/* steel tube (translucent so the wrap lines read clearly) */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[outerR, outerR, len, 64]} />
          <meshStandardMaterial color="#9AA0A6" metalness={0.7} roughness={0.4} transparent opacity={0.5} />
        </mesh>
        {/* front cap ring */}
        <mesh position={[0, 0, len / 2]}>
          <ringGeometry args={[innerR, outerR, 64]} />
          <meshStandardMaterial color="#9AA0A6" metalness={0.7} roughness={0.4} side={2} />
        </mesh>
        {/* helical tape wrap (edges) — spacing follows the lay */}
        {edges.map((e, i) => <Line key={i} points={e.pts} color={e.c} lineWidth={3} />)}

        <Html position={[0, outerR + 0.3, len / 2]} center>
          <div style={tag('#ffd166')}>BLR {layer.wide} × {layer.thickness} mm</div>
        </Html>
        <Html position={[0, -outerR * 1.7, 0]} center>
          <div style={{ color: '#9fb3c8', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            armouring · inner Ø {layer.innerD.toFixed(2)} → outer Ø {layer.outerD.toFixed(2)} mm · lay {pitchMm} mm
          </div>
        </Html>
      </Canvas>
    </div>
  );
};

export default Armour3D;
